import {
  IStepValueMatchExpectations,
  TWizardStepComponentEditable,
  TStepPayload,
  TStepErrors,
  WIZARD_STUDENT_STEPS_PATHS,
  TWizardStepComponentEditableView,
  IWizardStudentState
} from "../../../interfaces";
import { Form } from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { UserAPI } from "../../../../../API/userApi";
import { getChangedFields, getStepErrorsFromResponse } from "../../../utils";
import {
  StudentIdealMatchValues,
  StudentMatchCharacteristicsValues,
  TStudentMatchCharacteristics
} from "../../../../../interfaces/interfaces";
import { useContextHospi } from "../../../../../context/ContextHospi";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import {
  Quiet,
  BigHouse,
  CityCenter,
  FirePlace,
  HouseModern,
  OutdoorTrees,
  PetPaw,
  TrainStation,
  TreeHouse
} from "components/Icons/Wizard/DescribeAccommodation/AllAccommodation";
import { StepViewOnlyValue } from "../../styles";
import { WizardButtonSelect } from "../../../components";

const statePath = WIZARD_STUDENT_STEPS_PATHS.MatchExpectations;

export const MATCH_CHARACTERISTICS_ICONS: {
  [key in TStudentMatchCharacteristics]: React.ReactNode;
} = {
  quiet: <Quiet />,
  animal_friendly: <PetPaw />,
  city_center: <CityCenter />,
  close_public_transport: <TrainStation />,
  unique: <TreeHouse />,
  cosy: <FirePlace />,
  spacious: <BigHouse />,
  characteristic: <HouseModern />,
  lots_greenery: <OutdoorTrees />
};

export const WizardStepMatchExpectations: TWizardStepComponentEditable<IWizardStudentState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<
      TStepPayload<IStepValueMatchExpectations>
    >(state[statePath] ?? {});
    const [errors, setErrors] =
      useState<TStepErrors<IStepValueMatchExpectations>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);

        return UserAPI.wizardStudentSaveMatchExpectations(values)
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);

        return UserAPI.wizardStudentSaveMatchExpectationsPartially(
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepMatchExpectationsView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const Root = styled(Form)``;

export const WizardStepMatchExpectationsView: TWizardStepComponentEditableView<IStepValueMatchExpectations> =
  React.memo(({ values, setValues, errors, readonly, viewonly }) => {
    const { currentUserRole } = useContextHospi();
    const { t } = useTranslation(currentUserRole);

    const idealMatchOptions = StudentIdealMatchValues.map((v) => ({
      value: v,
      label: (
        t(
          `${currentUserRole}Profile.matchExpectations.form.idealMatch.options`,
          {
            returnObjects: true
          }
        ) as { [key: string]: string }
      )[v]
    }));

    const matchCharacteristicsOptions = StudentMatchCharacteristicsValues.map(
      (v) => ({
        value: v,
        label: (
          t(
            `${currentUserRole}Profile.matchExpectations.form.matchCharacteristics.options`,
            {
              returnObjects: true
            }
          ) as { [key: string]: string }
        )[v],
        icon: MATCH_CHARACTERISTICS_ICONS[v]
      })
    );

    return (
      <Root layout="vertical">
        <Form.Item
          label={t(
            `${currentUserRole}Profile.matchExpectations.form.idealMatch.title`
          )}
          validateStatus={errors?.ideal_match ? "error" : ""}
          help={errors?.ideal_match}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {idealMatchOptions
                .filter((c) => values.ideal_match?.includes(c.value))
                ?.map((o) => o.label)
                .join(", ") ?? ""}
            </StepViewOnlyValue>
          ) : (
            <WizardButtonSelect
              disabled={readonly}
              gridTemplate="1fr 1fr"
              value={values.ideal_match ?? undefined}
              options={idealMatchOptions}
              onChange={(newValue) =>
                setValues({
                  ...values,
                  ideal_match: newValue
                })
              }
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${currentUserRole}Profile.matchExpectations.form.matchCharacteristics.title`
          )}
          validateStatus={errors?.match_characteristics ? "error" : ""}
          help={errors?.match_characteristics}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {matchCharacteristicsOptions
                .filter((c) => values.match_characteristics?.includes(c.value))
                ?.map((o) => o.label)
                .join(", ") ?? ""}
            </StepViewOnlyValue>
          ) : (
            <WizardButtonSelect
              disabled={readonly}
              value={values.match_characteristics ?? undefined}
              options={matchCharacteristicsOptions}
              onChange={(newValue) =>
                setValues({
                  ...values,
                  match_characteristics: newValue
                })
              }
            />
          )}
        </Form.Item>
      </Root>
    );
  });
